const LLAMADA_HTTP = /^(GET|POST|PUT|DELETE|PATCH)\s+\S+/i;

export const tieneContenidoDetallable = (detalle, esLogin = false) => {
  if (esLogin) {
    return Boolean((detalle?.requestOutput || detalle?.requestInput || "").trim());
  }
  if (!detalle) {
    return false;
  }

  const output = (detalle.requestOutput || "").trim();
  if (output) {
    return true;
  }

  const input = (detalle.requestInput || "").trim();
  if (!input) {
    return false;
  }

  if (input.startsWith("{")) {
    try {
      const json = JSON.parse(input);
      if (json && typeof json === "object" && "url" in json) {
        const body = json.body;
        return body != null && body !== "";
      }
      return true;
    } catch (error) {
      // Sigue evaluando otros formatos.
    }
  }

  const { llamada, cuerpo } = separarLlamada(input);
  if (llamada) {
    return Boolean(cuerpo?.trim());
  }

  return false;
};

export const separarLlamada = (detalle) => {
  if (detalle == null || detalle === "") {
    return { llamada: "", cuerpo: "" };
  }

  const limpio = detalle.trim();
  if (limpio.startsWith("{")) {
    try {
      const json = JSON.parse(limpio);
      if (json && typeof json === "object" && "url" in json && "body" in json) {
        const cuerpo = json.body == null
          ? ""
          : typeof json.body === "string"
            ? json.body
            : JSON.stringify(json.body);
        return { llamada: json.url || "", cuerpo };
      }
    } catch (error) {
      // El texto no es el JSON { url, body }. Sigue el formato anterior.
    }
  }

  const salto = detalle.indexOf("\n");
  if (salto < 0) {
    return LLAMADA_HTTP.test(detalle.trim())
      ? { llamada: detalle.trim(), cuerpo: "" }
      : { llamada: "", cuerpo: detalle };
  }

  const primera = detalle.slice(0, salto).trim();
  if (LLAMADA_HTTP.test(primera)) {
    return { llamada: primera, cuerpo: detalle.slice(salto + 1) };
  }

  return { llamada: "", cuerpo: detalle };
};

export const presentar = (valor, formato) => {
  if (valor == null || String(valor).trim() === "") {
    return { texto: "", aviso: "No hay datos para mostrar.", tipo: "vacio" };
  }

  const crudo = String(valor);
  const limpio = crudo.trim();
  if (formato === "json") {
    try {
      return { texto: JSON.stringify(JSON.parse(limpio), null, 2), aviso: "", tipo: "json" };
    } catch (error) {
      return { texto: crudo, aviso: "El contenido no es JSON.", tipo: "texto" };
    }
  }

  if (limpio.startsWith("<")) {
    const doc = new DOMParser().parseFromString(limpio, "application/xml");
    if (doc.documentElement && !doc.querySelector("parsererror")) {
      return { texto: formatearXml(doc.documentElement, 0).replace(/\n$/, ""), aviso: "", tipo: "xml" };
    }
  }

  return { texto: crudo, aviso: "El contenido no es XML.", tipo: "texto" };
};

const formatearXml = (nodo, nivel) => {
  const sangria = "  ".repeat(nivel);
  const atributos = [...nodo.attributes].map((atributo) => ` ${atributo.name}="${atributo.value}"`).join("");
  const hijos = [...nodo.childNodes];
  const texto = hijos.length === 1 && hijos[0].nodeType === Node.TEXT_NODE ? hijos[0].textContent.trim() : "";
  if (texto) {
    return `${sangria}<${nodo.nodeName}${atributos}>${texto}</${nodo.nodeName}>\n`;
  }

  const contenido = hijos.map((hijo) => {
    if (hijo.nodeType === Node.ELEMENT_NODE) {
      return formatearXml(hijo, nivel + 1);
    }
    if (hijo.nodeType === Node.TEXT_NODE && hijo.textContent.trim()) {
      return `${"  ".repeat(nivel + 1)}${hijo.textContent.trim()}\n`;
    }
    return "";
  }).join("");

  if (!contenido) {
    return `${sangria}<${nodo.nodeName}${atributos} />\n`;
  }

  return `${sangria}<${nodo.nodeName}${atributos}>\n${contenido}${sangria}</${nodo.nodeName}>\n`;
};

export const tokenizar = (texto, tipo) => {
  if (!texto) {
    return [];
  }
  if (tipo === "json") {
    return tokenizarJson(texto);
  }
  if (tipo === "xml") {
    return tokenizarXml(texto);
  }
  return [{ tipo: "texto", texto }];
};

const tokenizarJson = (texto) => {
  const tokens = [];
  const expresion = /"(?:\\.|[^"\\])*"|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}\[\],:]|\s+/g;
  let ultimo = 0;
  let coincidencia = expresion.exec(texto);
  while (coincidencia) {
    if (coincidencia.index > ultimo) {
      tokens.push({ tipo: "texto", texto: texto.slice(ultimo, coincidencia.index) });
    }

    const parte = coincidencia[0];
    let tipoToken = "texto";
    if (parte.startsWith("\"")) {
      tipoToken = /^\s*:/.test(texto.slice(expresion.lastIndex)) ? "clave" : "string";
    } else if (parte === "true" || parte === "false" || parte === "null") {
      tipoToken = "literal";
    } else if (parte[0] === "-" || (parte[0] >= "0" && parte[0] <= "9")) {
      tipoToken = "numero";
    } else if (!/^\s+$/.test(parte)) {
      tipoToken = "signo";
    }

    tokens.push({ tipo: tipoToken, texto: parte });
    ultimo = expresion.lastIndex;
    coincidencia = expresion.exec(texto);
  }

  if (ultimo < texto.length) {
    tokens.push({ tipo: "texto", texto: texto.slice(ultimo) });
  }

  return tokens;
};

const tokenizarXml = (texto) => {
  const tokens = [];
  texto.split(/(<!--[\s\S]*?-->|<\/?[^>]+>)/g).forEach((parte) => {
    if (!parte) {
      return;
    }
    if (parte.startsWith("<!--")) {
      tokens.push({ tipo: "comentario", texto: parte });
      return;
    }
    if (parte.startsWith("<")) {
      parte.split(/("[^"]*"|'[^']*')/g).forEach((trozo) => {
        if (!trozo) {
          return;
        }
        tokens.push({ tipo: trozo.startsWith("\"") || trozo.startsWith("'") ? "string" : "etiqueta", texto: trozo });
      });
      return;
    }
    tokens.push({ tipo: "texto", texto: parte });
  });
  return tokens;
};
